import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

const PROJECT_TYPES = [
  "Reel Social",
  "Video Promozionale",
  "Matrimonio / Evento",
  "Intervista",
  "Case Study Aziendale",
  "Altro",
];

const BUDGETS = ["< 200€", "200–500€", "500–1000€", "1000–2500€", "> 2500€"];

const initial = {
  name: "",
  email: "",
  phone: "",
  business: "",
  city: "",
  project_type: PROJECT_TYPES[0],
  budget: BUDGETS[0],
  message: "",
};

export default function QuoteForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/quotes", form);
      setDone(true);
      setForm(initial);
      toast.success("Richiesta inviata! Ti rispondo entro 24 ore.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore invio richiesta");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="preventivo" data-testid="quote-section" className="py-24 sm:py-32 relative">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <div className="overline mb-4">Preventivo gratuito</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter">
              Raccontami
              <br />
              <span className="text-gold">il tuo progetto.</span>
            </h2>
            <p className="mt-6 text-white/60 leading-relaxed max-w-md">
              Rispondo entro 24 ore con un preventivo personalizzato. Nessun impegno.
            </p>
            <div className="mt-10 space-y-3 text-sm text-white/70">
              <div>📧 Guzerat1998@gmail.com</div>
              <div>📱 +39 327 425 64 32</div>
              <div>📍 Villongo (BG) — disponibile in tutta Italia</div>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="lg:col-span-7 p-8 md:p-10 rounded-xl border border-white/10 bg-graphite-900/60 backdrop-blur-sm space-y-5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nome e cognome *" name="name" value={form.name} onChange={onChange} required />
              <Field label="Email *" type="email" name="email" value={form.email} onChange={onChange} required />
              <Field label="Telefono / WhatsApp" name="phone" value={form.phone} onChange={onChange} />
              <Field label="Città" name="city" value={form.city} onChange={onChange} />
              <Field label="Attività / Azienda" name="business" value={form.business} onChange={onChange} className="md:col-span-2" />
              <Select label="Tipo di progetto *" name="project_type" value={form.project_type} onChange={onChange} options={PROJECT_TYPES} />
              <Select label="Budget indicativo" name="budget" value={form.budget} onChange={onChange} options={BUDGETS} />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">
                Descrivi il progetto *
              </label>
              <textarea
                name="message"
                required
                rows={5}
                value={form.message}
                onChange={onChange}
                data-testid="quote-input-message"
                placeholder="Es. Reel di 30s per il mio ristorante. Vorrei mostrare la preparazione della pasta fresca..."
                className="w-full bg-graphite-950 border border-white/10 rounded-md px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold transition-colors"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="quote-submit-button"
              className="btn-gold w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 strokeWidth={1.5} className="w-4 h-4 animate-spin" /> Invio...
                </>
              ) : (
                <>
                  Invia richiesta <Send strokeWidth={1.5} className="w-4 h-4" />
                </>
              )}
            </button>
            {done && (
              <div
                data-testid="quote-success"
                className="text-sm text-gold border border-gold/30 bg-gold/5 rounded-md px-4 py-3"
              >
                ✦ Richiesta inviata. Ti rispondo entro 24 ore.
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, className = "", ...rest }) {
  return (
    <div className={className}>
      <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">{label}</label>
      <input
        name={name}
        data-testid={`quote-input-${name}`}
        {...rest}
        className="w-full bg-graphite-950 border border-white/10 rounded-md px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold transition-colors"
      />
    </div>
  );
}

function Select({ label, name, value, onChange, options }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">{label}</label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        data-testid={`quote-select-${name}`}
        className="w-full bg-graphite-950 border border-white/10 rounded-md px-4 py-3 text-white focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold transition-colors"
      >
        {options.map((o) => (
          <option key={o} value={o} className="bg-graphite-950">
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
